// Audit log of blocked-fetch attempts. One row per request that
// hit a security block.

import { ShieldAlert, RefreshCw } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { type SecurityAuditRow, useSecurityAudit } from '../../api/security';

export function SecurityAudit() {
  const { data, isLoading, mutate } = useSecurityAudit(200);

  return (
    <SecurityShell
      eyebrow="Audit · supply-chain"
      title="Security audit log"
      description={
        <>
          Every fetch attempt that matched a security block. Audit rows
          survive block deletion — when a block is removed, its row here
          shows <span className="orb-code">(block deleted)</span> in the
          reason column.
        </>
      }
    >
      <Card>
        <CardHeader
          title={`${data?.length ?? 0} blocked attempts`}
          description="Polled every 15 seconds. Limited to the most recent 200 rows."
          actions={
            <button
              type="button"
              onClick={() => mutate()}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted whitespace-nowrap">When</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Path</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Block type</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Reason</th>
                <th className="px-5 py-3 font-medium text-fg-muted">CVE</th>
                <th className="px-5 py-3 font-medium text-fg-muted">From</th>
              </tr>
            </thead>
            <tbody>
              <AuditTableRows isLoading={isLoading} data={data} />
            </tbody>
          </table>
        </div>
      </Card>
    </SecurityShell>
  );
}

interface AuditTableRowsProps {
  isLoading: boolean;
  data: SecurityAuditRow[] | undefined;
}

function AuditTableRows({ isLoading, data }: AuditTableRowsProps) {
  if (isLoading) {
    return (
      <tr>
        <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
          Loading…
        </td>
      </tr>
    );
  }
  if (!data?.length) {
    return (
      <tr>
        <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
          <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
          No blocked attempts yet — either no blocks are active, or no client has tried to fetch a blocked artifact.
        </td>
      </tr>
    );
  }
  return (
    <>
      {data.map((row) => (
        <tr key={row.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
            {new Date(row.blocked_at).toLocaleString()}
          </td>
          <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">
            {row.artifact_path}
          </td>
          <td className="px-5 py-3">
            {row.block_type ? (
              <Badge tone="danger">{row.block_type}</Badge>
            ) : (
              <span className="text-fg-subtle italic text-xs">(deleted)</span>
            )}
          </td>
          <td className="px-5 py-3 text-fg-muted text-xs max-w-sm">
            {row.reason}
          </td>
          <td className="px-5 py-3">
            {row.cve_id ? (
              <span className="orb-code text-xs">{row.cve_id}</span>
            ) : <span className="text-fg-subtle">—</span>}
          </td>
          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
            {row.requester_ip || '—'}
          </td>
        </tr>
      ))}
    </>
  );
}
